/**
 * Тонкая обёртка над fetch для HTTP API sl1ce.
 *
 * Токен хранится в localStorage и подставляется в заголовок Authorization.
 * Ответ 401 на авторизованный запрос сбрасывает токен и передаёт управление
 * обработчику, который регистрирует приложение (переход на страницу входа).
 *
 * Выбранная база 1С уходит в заголовке X-Source-Id: с ней работают каталог,
 * создание заданий и дамп.
 */

const BASE = "/api";
const TOKEN_KEY = "sl1ce.token";
const SOURCE_KEY = "sl1ce.source";

export type QueryScalar = string | number | boolean;
export type QueryValue = QueryScalar | QueryScalar[] | null | undefined;
export type Query = Record<string, QueryValue>;

export interface RequestOptions {
  query?: Query;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly hint: string | null;
  readonly code: string;

  constructor(
    status: number,
    detail: string,
    hint: string | null = null,
    code = "error",
  ) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.hint = hint;
    this.code = code;
  }

  get offline(): boolean {
    return this.status === 0;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function describeError(error: unknown): {
  detail: string;
  hint: string | null;
} {
  if (isApiError(error)) return { detail: error.detail, hint: error.hint };
  if (error instanceof Error) return { detail: error.message, hint: null };
  return { detail: "Неизвестная ошибка", hint: null };
}

let token: string | null = localStorage.getItem(TOKEN_KEY);
let sourceId: string | null = localStorage.getItem(SOURCE_KEY);
let unauthorizedHandler: (() => void) | null = null;

export function getSourceId(): string | null {
  return sourceId;
}

export function setSourceId(value: string | null): void {
  sourceId = value;
  if (value) localStorage.setItem(SOURCE_KEY, value);
  else localStorage.removeItem(SOURCE_KEY);
}

export function getToken(): string | null {
  return token;
}

export function hasToken(): boolean {
  return token !== null && token !== "";
}

export function setToken(value: string | null): void {
  token = value;
  if (value) localStorage.setItem(TOKEN_KEY, value);
  else localStorage.removeItem(TOKEN_KEY);
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

const STATUS_FALLBACK: Readonly<Record<number, string>> = {
  400: "Некорректный запрос",
  401: "Нужен вход в сервис",
  403: "Недостаточно прав",
  404: "Объект не найден",
  409: "Конфликт состояния",
  422: "Не удалось разобрать параметры запроса",
  500: "Внутренняя ошибка сервера",
  502: "Сервер 1С недоступен или ответил ошибкой",
  503: "Сервис временно недоступен",
  504: "Истекло время ожидания ответа",
};

function buildQuery(query: Query | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item === "") continue;
      params.append(key, String(item));
    }
  }
  const search = params.toString();
  return search ? `?${search}` : "";
}

function describeDetail(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (item as { msg?: unknown })?.msg)
      .filter((msg): msg is string => typeof msg === "string");
    if (parts.length) return parts.join("; ");
  }
  return null;
}

async function toApiError(response: Response): Promise<ApiError> {
  const fallback =
    STATUS_FALLBACK[response.status] ?? `Ошибка ${response.status}`;
  let detail = fallback;
  let hint: string | null = null;
  let code = "error";

  try {
    const payload: unknown = await response.json();
    if (payload && typeof payload === "object") {
      const body = payload as Record<string, unknown>;
      detail = describeDetail(body.detail) ?? fallback;
      if (typeof body.hint === "string" && body.hint.trim()) hint = body.hint;
      if (typeof body.code === "string" && body.code.trim()) code = body.code;
    }
  } catch {
    code = "malformed_response";
  }

  return new ApiError(response.status, detail, hint, code);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const authorized = hasToken();
  if (authorized) headers.Authorization = `Bearer ${token}`;
  if (sourceId) headers["X-Source-Id"] = sourceId;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}${buildQuery(options.query)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new ApiError(
      0,
      "Не удалось связаться с сервером sl1ce",
      "Проверьте, запущен ли бэкенд на localhost:8000.",
      "network",
    );
  }

  if (response.status === 401 && authorized) {
    setToken(null);
    unauthorizedHandler?.();
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export const api = {
  get<T>(
    path: string,
    query?: Query,
    options: RequestOptions = {},
  ): Promise<T> {
    return request<T>("GET", path, undefined, {
      ...options,
      query: query ?? options.query,
    });
  },
  post<T>(
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    return request<T>("POST", path, body, options);
  },
  patch<T>(
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    return request<T>("PATCH", path, body, options);
  },
  del<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return request<T>("DELETE", path, undefined, options);
  },
};
